import { Dialog, DialogActions, DialogContent, Button, DialogTitle } from '@mui/material';
import { Formik } from 'formik';
import { Fragment, useMemo } from 'react';

import { SelectOption, USER_AFFILIATIONS_OPTIONS, UserAffiliations } from '@jebel/constants';

import { ContentRestrictionFragment, useUpdateContentRestrictionMutation } from 'shared/graphql';
import { useToast } from 'shared/hooks';

import { ContentRestrictionsForm, ContentRestrictionsFormValues } from '../ContentRestrictionForm';

const SAVING_MESSAGE_KEY = 'SAVING_SETTINGS_COMMUNITY_CONTENT_RESTRICTIONS';

interface Props {
  /**
   * The selected restriction.
   */
  selectedRestriction: ContentRestrictionFragment;

  /**
   * Whether the modal is open.
   */
  isOpen: boolean;

  /**
   * Callback to close the modal.
   */
  onClose: () => void;
}

export function EditContentRestrictionModal(props: Props) {
  const { showMessage, showSuccess, showError, dismiss } = useToast();

  const [updateRestriction] = useUpdateContentRestrictionMutation({
    refetchQueries: ['ListCommunityContentRestrictions'],
    awaitRefetchQueries: true,
  });

  const initials = useMemo(() => {
    const { description, isActive, audience, scope } = props.selectedRestriction;

    const initials: ContentRestrictionsFormValues = {
      description,
      isActive,
      audience: {},
      scope: {},
    };

    if (audience.affiliations) {
      const options = audience.affiliations
        .map(affiliation => USER_AFFILIATIONS_OPTIONS.find(option => option.value === affiliation))
        .filter(Boolean) as SelectOption<UserAffiliations>[];

      initials.audience.affiliations = options;
    }

    if (scope.creatorAffiliations) {
      const options = scope.creatorAffiliations
        .map(affiliation => USER_AFFILIATIONS_OPTIONS.find(option => option.value === affiliation))
        .filter(Boolean) as SelectOption<UserAffiliations>[];

      initials.scope.creatorAffiliations = options;
    }

    return initials;
  }, [props.selectedRestriction]);

  const handleSubmit = async (values: ContentRestrictionsFormValues) => {
    try {
      showMessage(`Saving content restrictions, please stand by...`, {
        id: SAVING_MESSAGE_KEY,
      });

      const audienceAffiliations = values.audience.affiliations?.map(
        affiliation => affiliation.value,
      );

      const scopeCreatorAffiliations = values.scope.creatorAffiliations?.map(
        affiliation => affiliation.value,
      );

      await updateRestriction({
        variables: {
          id: props.selectedRestriction.id,
          description: values.description,
          isActive: values.isActive,
          audience: { affiliations: audienceAffiliations },
          scope: { creatorAffiliations: scopeCreatorAffiliations },
        },
      });

      showSuccess(`Successfully updated restriction "${values.description}"`);
      props.onClose();
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(SAVING_MESSAGE_KEY);
    }
  };

  return (
    <Dialog maxWidth="sm" open={props.isOpen} onClose={props.onClose} fullWidth>
      <Formik initialValues={initials} onSubmit={handleSubmit}>
        {form => (
          <Fragment>
            <DialogTitle>Edit "{form.values.description}"</DialogTitle>

            <DialogContent dividers>
              <ContentRestrictionsForm />
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" disabled={form.isSubmitting} onClick={props.onClose}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={form.isSubmitting || !form.dirty}
                onClick={form.submitForm}
              >
                Save
              </Button>
            </DialogActions>
          </Fragment>
        )}
      </Formik>
    </Dialog>
  );
}
